/* Direct Create — packing lists made straight from the Order Register slip
 * window. Indigo colour coding keeps them apart from "Use invoice" output. */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ReadyBadge } from "./ReadyBadge";
import { ArrowDownToLine, ChevronDown, ChevronRight, Search, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, getAuthHeader } from "@/App";

export const DIRECT_ROW_CLS = "bg-indigo-50/80 hover:bg-indigo-100/80 shadow-[inset_4px_0_0_0_#6366f1]";

export const DirectBadge = ({ testId }) => (
  <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold bg-indigo-600 text-white rounded px-1 py-px align-middle"
        title="Created directly from the Order Register packing slip"
        data-testid={testId}>
    <Zap className="w-2.5 h-2.5" />DIRECT
  </span>
);

export const useDirectCreated = () => {
  const [dc, setDc] = useState({ keys: new Set(), pl: new Set(), bd: new Set() });
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/order-register/direct-created`, { headers: getAuthHeader() });
      setDc({ keys: new Set(r.data.invoice_keys || []), pl: new Set(r.data.packing_list_ids || []),
              bd: new Set(r.data.bulk_data_ids || []) });
    } catch { /* highlight is best-effort */ }
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("order-register:invoice-changed", load);
    return () => window.removeEventListener("order-register:invoice-changed", load);
  }, [load]);
  return dc;
};

export const isDirectEntry = (dc, tool, entry) => {
  if (tool === "packing_list") return dc.pl.has(entry.id);
  if (tool === "bulk_data") return dc.bd.has(entry.id);
  const inv = String(entry.matched_invoice_no || entry.invoice_no || "").trim().toUpperCase();
  return !!inv && dc.keys.has(inv);
};

export const originMatch = (dc, tool, entry, origin) =>
  origin === "all" || (origin === "direct") === isDirectEntry(dc, tool, entry);

const ORIGINS = [
  { id: "all", label: "All" },
  { id: "use", label: "Use Invoice", title: "Use-invoice + manual uploads" },
  { id: "direct", label: "Direct Create" },
];

export const OriginFilter = ({ value, onChange, testId }) => (
  <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white overflow-hidden"
        data-testid={`origin-filter-${testId}`}>
    {ORIGINS.map(o => (
      <button key={o.id} type="button" title={o.title || o.label} onClick={() => onChange(o.id)}
              data-testid={`origin-filter-${o.id}-${testId}`}
              className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${value === o.id
                ? (o.id === "direct" ? "bg-indigo-600 text-white" : "bg-slate-700 text-white")
                : "text-slate-600 hover:bg-indigo-50"}`}>
        {o.label}
      </button>
    ))}
  </span>
);

const DirectChip = ({ inv, tool, testId, busy, onUse, history }) => {
  const used = (inv.uses_tool || {})[tool] || 0;
  const when = inv.direct_at ? new Date(inv.direct_at).toLocaleString() : "";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px]"
          title={`Direct created${inv.direct_by ? ` by ${inv.direct_by}` : ""}${when ? ` · ${when}` : ""}${inv.direct_status === "overridden" ? " · admin override" : ""}`}
          data-testid={`direct-invoice-chip-${testId}-${inv.invoice_key}`}>
      <Zap className="w-3 h-3 text-indigo-500" />
      <span className="font-mono font-bold text-indigo-800">{inv.invoice_no}</span>
      <InTableBadge inv={inv} history={history} tool={tool} testId={testId} />
                  <ReadyBadge invoiceNo={inv.invoice_no || inv.invoice_key} testId={`chip-${testId}`} />
      {(inv.codes || []).length > 0 && <span className="text-slate-500">{inv.codes.join("/")}</span>}
      <span className="text-slate-500">· {inv.parcel_count - (inv.cancelled_parcels || 0)} parcel{inv.parcel_count - (inv.cancelled_parcels || 0) === 1 ? "" : "s"}</span>
      {inv.cancelled_parcels > 0 && (
        <span className="text-[9px] font-bold bg-rose-100 text-rose-700 border border-rose-300 rounded-full px-1.5"
              data-testid={`direct-cancelled-count-${testId}-${inv.invoice_key}`}>−{inv.cancelled_parcels} cancelled</span>)}
      {inv.label_pending && (
        <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-1.5">label pending</span>
      )}
      {used > 0 && (
        <span className="text-[9px] font-bold bg-white text-indigo-700 border border-indigo-300 rounded-full px-1.5"
              data-testid={`direct-use-count-${testId}-${inv.invoice_key}`}>×{used}</span>
      )}
      <Button size="sm" className="h-6 px-2 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={busy} onClick={() => onUse(inv)}
              data-testid={`direct-use-invoice-${testId}-${inv.invoice_key}`}>
        Use
      </Button>
    </span>
  );
};

export const DirectCreateBox = ({ invoices, tool, testId, busy, onUse, scopeLabel, history }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const t = q.trim().toLowerCase();
  const list = t ? invoices.filter(i => i.invoice_no.toLowerCase().includes(t) ||
      (i.codes || []).join(" ").toLowerCase().includes(t) ||
      (i.customer_name || "").toLowerCase().includes(t)) : invoices;
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50" data-testid={`direct-create-box-${testId}`}>
      <div role="button" tabIndex={0} onClick={() => setOpen(o => !o)}
           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o => !o); } }}
           className="w-full flex items-center gap-2 px-3 py-2 cursor-pointer"
           data-testid={`direct-create-toggle-${testId}`}>
        <Zap className="w-4 h-4 text-indigo-600 shrink-0" />
        <span className="text-xs font-semibold text-indigo-800">Direct Create Packing Lists ({scopeLabel})</span>
        <span className="text-[10px] font-bold text-indigo-700 bg-white border border-indigo-200 rounded-full px-1.5"
              data-testid={`direct-create-count-${testId}`}>{invoices.length}</span>
        <span className="text-[10px] text-indigo-500 hidden md:inline truncate">— created from the Order Register packing slip</span>
        <span className="ml-auto text-indigo-600">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`direct-create-body-${testId}`}>
          <div className="relative max-w-xs">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice / client / customer"
                   className="h-7 pl-7 text-xs bg-white" data-testid={`direct-create-search-${testId}`} />
          </div>
          {list.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic" data-testid={`direct-create-empty-${testId}`}>
              {invoices.length ? "No direct-created invoices match the search." : "No direct-created packing lists in this window."}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {list.map(inv => <DirectChip key={inv.invoice_key} inv={inv} tool={tool} testId={testId} busy={busy} onUse={onUse} history={history} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ROW_PREFIX = { packing_list: "history-row-", bulk_data: "bulk-history-row-", pack_team: "pt-history-row-", bulk_label: "bl-history-row-" };

const findEntries = (history, inv) => {
  const keys = [inv.invoice_no, inv.invoice_key].filter(Boolean).map(x => String(x).trim().toUpperCase());
  return (history || []).filter(e => !e.superseded_by &&
    keys.includes(String(e.matched_invoice_no || e.invoice_no || "").trim().toUpperCase()))
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
};

export const jumpToHistoryRow = (tool, id) => {
  window.dispatchEvent(new CustomEvent("history-jump", { detail: { tool } }));
  setTimeout(() => {
    const el = document.querySelector(`[data-testid="${ROW_PREFIX[tool]}${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const prev = el.style.cssText;
    el.style.transition = "background-color 0.4s, box-shadow 0.4s";
    el.style.backgroundColor = "#e0f2fe";
    el.style.boxShadow = "inset 4px 0 0 #0284c7, 0 0 0 2px #38bdf8";
    setTimeout(() => { el.style.cssText = prev; }, 2600);
  }, 150);
};

/* "In table ↓" / "Not in table" note beside the invoice number on chips. */
export const InTableBadge = ({ inv, history, tool, testId }) => {
  if (!history) return null;
  const hits = findEntries(history, inv);
  if (!hits.length) {
    return (
      <span className="text-[9px] font-semibold text-slate-400 border border-dashed border-slate-300 rounded-full px-1.5"
            title="No current file for this invoice in the table below yet"
            data-testid={`not-in-table-${testId}-${inv.invoice_key}`}>Not in table</span>);
  }
  return (
    <button type="button" onClick={() => jumpToHistoryRow(tool, hits[0].id)}
            className="inline-flex items-center gap-0.5 text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-300 rounded-full px-1.5 hover:bg-sky-100 transition-colors"
            title={`Available in the table below${hits.length > 1 ? ` (${hits.length} files — jumps to the latest)` : ""} — click to go to the row`}
            data-testid={`in-table-${testId}-${inv.invoice_key}`}>
      <ArrowDownToLine className="w-2.5 h-2.5" />In table{hits.length > 1 ? ` ×${hits.length}` : ""}
    </button>);
};
